import { Injectable, OnDestroy, signal } from '@angular/core';
import { AuthService } from './auth.service';

const WARNING_AFTER_MS = 15 * 60 * 1000;
const LOGOUT_COUNTDOWN_SECONDS = 5 * 60;

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  readonly warningVisible = signal(false);
  readonly countdownSeconds = signal(LOGOUT_COUNTDOWN_SECONDS);

  private readonly activityEvents: (keyof WindowEventMap)[] = [
    'click',
    'keydown',
    'mousedown',
    'mousemove',
    'scroll',
    'touchstart'
  ];

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private listening = false;

  constructor(private readonly authService: AuthService) {}

  start(): void {
    if (this.listening) {
      this.resetIdleTimer();
      return;
    }

    this.listening = true;
    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.onUserActivity, { passive: true });
    });
    this.resetIdleTimer();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  stop(): void {
    if (this.listening) {
      this.activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.onUserActivity);
      });
      this.listening = false;
    }

    this.clearIdleTimer();
    this.clearCountdownTimer();
    this.warningVisible.set(false);
    this.countdownSeconds.set(LOGOUT_COUNTDOWN_SECONDS);
  }

  continueSession(): void {
    this.warningVisible.set(false);
    this.countdownSeconds.set(LOGOUT_COUNTDOWN_SECONDS);
    this.clearCountdownTimer();
    this.resetIdleTimer();
  }

  logoutNow(): void {
    this.stop();
    this.authService.logout();
  }

  private readonly onUserActivity = (): void => {
    if (this.warningVisible()) {
      return;
    }
    this.resetIdleTimer();
  };

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.showWarningAndStartCountdown();
    }, WARNING_AFTER_MS);
  }

  private showWarningAndStartCountdown(): void {
    this.warningVisible.set(true);
    this.countdownSeconds.set(LOGOUT_COUNTDOWN_SECONDS);
    this.clearCountdownTimer();

    this.countdownTimer = setInterval(() => {
      const remaining = this.countdownSeconds();
      if (remaining <= 1) {
        this.logoutNow();
        return;
      }
      this.countdownSeconds.set(remaining - 1);
    }, 1000);
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}